import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { alpha, useTheme } from "@mui/material/styles";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import FlightTakeoffOutlinedIcon from "@mui/icons-material/FlightTakeoffOutlined";
import DirectionsBoatOutlinedIcon from "@mui/icons-material/DirectionsBoatOutlined";
import AltRouteOutlinedIcon from "@mui/icons-material/AltRouteOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import {
  getA3FamiliasTalla,
  getA3FamiliasTallaClasificadas,
  getA3PedidoLineasMatriz,
  getA3Tallas,
  searchA3Articulos,
} from "../../api/Produccion/a3ArticulosApi";
import {
  getLogisticaEnviosPorPedido,
  createLogisticaEnvio,
  deleteLogisticaEnvio,
  getLogisticaEnvioConsolidacionRef,
} from "../../api/Logistica/logisticaEnviosApi";
import { getLogisticsByProductId } from "../../api/Logistica/logisticsApi";
import { getTimelineByProductId } from "../../api/Estimates/estimatesApi";
import { getForwarderProveedores } from "../../api/ProveedoresyFabricas/proveedoresApi";
import DocumentosLogistica from "../Logistica/DocumentosLogistica";
import EnvioMatrizLectura from "../Logistica/EnvioMatrizLectura";
import LogisticaEnvioEditDialog from "../Logistica/LogisticaEnvioEditDialog";
import LogisticaPedidosMatricesReferencia from "../Logistica/LogisticaPedidosMatricesReferencia";
import { buildEnvioLineasSavePayload } from "../Logistica/logisticaEnvioEditUtils";
import {
  normStr,
  packLinepediFromGridPure,
  lineasPackMatch,
  matrizToNumber,
  fmtQtyMatriz,
  tallaLabel,
  colorLabel,
  sortFamiliesByKind,
} from "./a3PedidoMatrizUtils";

const SHIPMENT_METHOD_OPTIONS = [
  "Air",
  "Sea",
  "Sea-Air",
  "Truck",
  "Courier",
  "Combination",
];
const CRITICAL_DATE_INPUTS = [
  { field: "InspectionDate", label: "Inspection date" },
  { field: "ExFD", label: "ExFD" },
  { field: "ETDF", label: "ETDF" },
  { field: "CutOff", label: "CUT-OFF" },
];
const TIMELINE_DATE_TASK_TO_FIELD = {
  "Inspection date": "InspectionDate",
  ExFD: "ExFD",
  ETDF: "ETDF",
  "CUT-OFF": "CutOff",
  ETD: "ETD",
  DD: "DD",
  "Booking Done": "BookingDone",
  ETA: "ETA",
  "Documents Approved": "DocumentsApproved",
  "Original Telex Docs Semt": "OriginalTelexDocsSemt",
};

function emptyEnvioHeader() {
  return {
    ETA: "",
    Forwarder: "",
    POReference: "",
    Remarks: "",
    ShipmentMethod: "",
    NCartons: "",
    GrossWeightKgs: "",
    TotalVolumeCbm: "",
    BookingDone: "",
    InspectionDate: "",
    ExFD: "",
    ETDF: "",
    CutOff: "",
    ETD: "",
    DD: "",
    ShipmentCodeCustomer: "",
    BLAWBNr: "",
    DocumentsApproved: "",
    OriginalTelexDocsSemt: "",
  };
}

/** PO del pedido CRM/A3: columna explícita o, por defecto, Referencia del pedido. */
function poReferenceFromPedidoRow(row) {
  if (!row) return "";
  const direct = row.POReference ?? row.PORef ?? row.poReference;
  if (direct != null && String(direct).trim()) return String(direct).trim();
  if (row.Referencia != null && String(row.Referencia).trim())
    return String(row.Referencia).trim();
  return "";
}

function apiDateFromInput(s) {
  const t = String(s || "").trim();
  return t || null;
}

function parseOptionalInt(s) {
  const n = parseInt(String(s || "").replace(/\s/g, ""), 10);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function parseOptionalDecimal(s) {
  const t = String(s ?? "")
    .replace(",", ".")
    .trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function fmtEnvioDate(v) {
  if (v == null || v === "") return "—";
  return String(v).slice(0, 10);
}

function inputDate(v) {
  if (v == null || v === "") return "";
  return String(v).slice(0, 10);
}

function pickTimelineDate(task) {
  return inputDate(task?.RealDate || task?.UpdatedDate || task?.ExpectedDate);
}

function cabeceraFromEnvioYConsolidacion(envio, consolidacion) {
  if (!consolidacion?.productosConReferencia) return envio;
  let shipmentMethodFallback = "";
  for (const row of consolidacion.porPedido || []) {
    const sm = row?.productionLogistics?.ShipmentMethod;
    if (sm && String(sm).trim()) {
      shipmentMethodFallback = String(sm).trim();
      break;
    }
  }
  return {
    ...envio,
    NCartons:
      consolidacion.sumaNCartons != null
        ? Math.round(consolidacion.sumaNCartons)
        : envio.NCartons,
    GrossWeightKgs:
      consolidacion.sumaGrossWeightKgs != null
        ? consolidacion.sumaGrossWeightKgs
        : envio.GrossWeightKgs,
    TotalVolumeCbm:
      consolidacion.sumaTotalVolumeCbm != null
        ? consolidacion.sumaTotalVolumeCbm
        : envio.TotalVolumeCbm,
    ETA: consolidacion.etaMasTarde || envio.ETA,
    BookingDone: consolidacion.bookingMasTarde || envio.BookingDone,
    InspectionDate:
      consolidacion.inspectionDateMasTarde || envio.InspectionDate,
    ExFD: consolidacion.exfdMasTarde || envio.ExFD,
    ETDF: consolidacion.etdfMasTarde || envio.ETDF,
    CutOff: consolidacion.cutOffMasTarde || envio.CutOff,
    ETD: consolidacion.etdMasTarde || envio.ETD,
    DD: consolidacion.ddMasTarde || envio.DD,
    DocumentsApproved:
      consolidacion.documentsApprovedMasTarde || envio.DocumentsApproved,
    OriginalTelexDocsSemt:
      consolidacion.originalTelexMasTarde || envio.OriginalTelexDocsSemt,
    ShipmentMethod: shipmentMethodFallback || envio.ShipmentMethod,
  };
}

function getShipmentMethodMeta(method, theme) {
  const raw = String(method ?? "").trim();
  const normalized = raw.toLowerCase();
  const isSeaAir =
    normalized.includes("sea-air") ||
    normalized.includes("sea air") ||
    normalized.includes("sea/air") ||
    normalized.includes("air/sea") ||
    normalized.includes("air-sea");
  if (isSeaAir) {
    return {
      label: raw || "Sea-Air",
      color: "#14b8a6",
      icon: <AltRouteOutlinedIcon sx={{ fontSize: 20 }} />,
    };
  }
  if (normalized.includes("courier") || normalized.includes("mensaj")) {
    return {
      label: raw || "Courier",
      color: "#0f766e",
      icon: <Inventory2OutlinedIcon sx={{ fontSize: 20 }} />,
    };
  }
  if (
    normalized.includes("air") ||
    normalized.includes("aer") ||
    normalized.includes("flight")
  ) {
    return {
      label: raw || "Air",
      color: "#0ea5e9",
      icon: <FlightTakeoffOutlinedIcon sx={{ fontSize: 20 }} />,
    };
  }
  if (
    normalized.includes("sea") ||
    normalized.includes("mar") ||
    normalized.includes("boat")
  ) {
    return {
      label: raw || "Sea",
      color: "#2563eb",
      icon: <DirectionsBoatOutlinedIcon sx={{ fontSize: 20 }} />,
    };
  }
  if (
    normalized.includes("comb") ||
    normalized.includes("mix") ||
    normalized.includes("multi")
  ) {
    return {
      label: raw || "Combination",
      color: "#8b5cf6",
      icon: <AltRouteOutlinedIcon sx={{ fontSize: 20 }} />,
    };
  }
  return {
    label: raw || "Truck",
    color: "#ea580c",
    icon: <LocalShippingOutlinedIcon sx={{ fontSize: 20 }} />,
  };
}

const PedidoEnviosDialog = ({
  open,
  onClose,
  productId,
  pedidoRow,
  presupuesto,
  /** Opcional: maestro A3 ya resuelto (p. ej. flujo alta desde estimate). */
  articulo = null,
  onChanged = null,
  /** Si true, al abrir entra directo en el formulario de nuevo envío. */
  autoOpenNuevo = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [envios, setEnvios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [editEnvio, setEditEnvio] = useState(null);
  const [deleteEnvio, setDeleteEnvio] = useState(null);
  const [deletingEnvio, setDeletingEnvio] = useState(false);
  const [header, setHeader] = useState(() => emptyEnvioHeader());

  const [familiasTallaCols, setFamiliasTallaCols] = useState([]);
  const [familiasTallaRows, setFamiliasTallaRows] = useState([]);
  const [matrixFamiliaH, setMatrixFamiliaH] = useState("");
  const [matrixFamiliaV, setMatrixFamiliaV] = useState("");
  const [tallasH, setTallasH] = useState([]);
  const [tallasV, setTallasV] = useState([]);
  const [lineasTallas, setLineasTallas] = useState([]);
  const [articuloMaestro, setArticuloMaestro] = useState(null);
  const [loadingMatriz, setLoadingMatriz] = useState(false);
  const [loadingPrefill, setLoadingPrefill] = useState(false);
  const [forwarderOptions, setForwarderOptions] = useState([]);
  const [consolidacionByEnvio, setConsolidacionByEnvio] = useState({});

  const idPresupuestoA3Pedido = pedidoRow?.IdPresupuestoA3Pedido;
  /** Id de producto CRM (no confundir con IdPresupuesto). */
  const productIdDisplay =
    Number(presupuesto?.ProductId) ||
    Number(presupuesto?.IdProducto) ||
    Number(productId) ||
    null;
  const dbSource = String(pedidoRow?.EmpresaA3 || "STAGE_DESIGN").trim();
  const softFieldSx = {
    "& .MuiInputLabel-root": {
      fontWeight: 600,
      color: theme.palette.text.secondary,
    },
    "& .MuiInputBase-root": {
      backgroundColor: alpha(
        theme.palette.background.paper,
        theme.palette.mode === "dark" ? 0.52 : 0.98,
      ),
      borderRadius: 1.8,
      boxShadow:
        theme.palette.mode === "dark"
          ? "0 1px 0 rgba(255,255,255,0.03) inset"
          : "0 1px 2px rgba(15,23,42,0.04)",
      "& fieldset": {
        borderColor: alpha(theme.palette.primary.main, 0.14),
      },
      "&:hover fieldset": {
        borderColor: alpha(theme.palette.primary.main, 0.28),
      },
      "&.Mui-focused": {
        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.08)}`,
      },
    },
  };
  const notifyChanged = useCallback(async () => {
    if (typeof onChanged === "function") {
      await onChanged();
    }
  }, [onChanged]);

  const load = useCallback(async () => {
    if (!idPresupuestoA3Pedido) return;
    setLoading(true);
    setError("");
    try {
      const data = await getLogisticaEnviosPorPedido(idPresupuestoA3Pedido);
      setEnvios(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [idPresupuestoA3Pedido]);

  useEffect(() => {
    if (open && idPresupuestoA3Pedido) load();
  }, [open, idPresupuestoA3Pedido, load]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    getForwarderProveedores()
      .then((rows) => {
        if (!cancelled) setForwarderOptions(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setForwarderOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open || envios.length === 0) {
      setConsolidacionByEnvio({});
      return;
    }
    let cancelled = false;
    (async () => {
      const targets = envios.filter(
        (e) => (Array.isArray(e?.pedidoLinks) ? e.pedidoLinks.length : 0) >= 2,
      );
      if (targets.length === 0) {
        if (!cancelled) setConsolidacionByEnvio({});
        return;
      }
      const pairs = await Promise.all(
        targets.map(async (e) => {
          try {
            const data = await getLogisticaEnvioConsolidacionRef(
              e.IdLogisticaEnvio,
            );
            return [e.IdLogisticaEnvio, data];
          } catch (_err) {
            return [e.IdLogisticaEnvio, null];
          }
        }),
      );
      if (cancelled) return;
      const next = {};
      for (const [id, data] of pairs) next[id] = data;
      setConsolidacionByEnvio(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, envios]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const [familiasData, familiasClasData] = await Promise.all([
          getA3FamiliasTalla("", dbSource),
          getA3FamiliasTallaClasificadas("", dbSource),
        ]);
        if (cancelled) return;
        const famAll = Array.isArray(familiasData?.rows)
          ? familiasData.rows
          : [];
        const cols = Array.isArray(familiasClasData?.rowsTalla)
          ? familiasClasData.rowsTalla
          : [];
        const rowsFam = Array.isArray(familiasClasData?.rowsColor)
          ? familiasClasData.rowsColor
          : [];
        setFamiliasTallaCols(cols.length ? cols : famAll);
        setFamiliasTallaRows(rowsFam.length ? rowsFam : famAll);
      } catch (_e) {
        if (!cancelled) {
          setFamiliasTallaCols([]);
          setFamiliasTallaRows([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, dbSource]);

  useEffect(() => {
    if (!newOpen || !matrixFamiliaH) {
      setTallasH([]);
      return;
    }
    let active = true;
    getA3Tallas(matrixFamiliaH, "", dbSource)
      .then((data) => {
        if (!active) return;
        setTallasH(Array.isArray(data?.rows) ? data.rows : []);
      })
      .catch(() => {
        if (active) setTallasH([]);
      });
    return () => {
      active = false;
    };
  }, [newOpen, matrixFamiliaH, dbSource]);

  useEffect(() => {
    if (!newOpen || !matrixFamiliaV) {
      setTallasV([]);
      return;
    }
    let active = true;
    getA3Tallas(matrixFamiliaV, "", dbSource)
      .then((data) => {
        if (!active) return;
        setTallasV(Array.isArray(data?.rows) ? data.rows : []);
      })
      .catch(() => {
        if (active) setTallasV([]);
      });
    return () => {
      active = false;
    };
  }, [newOpen, matrixFamiliaV, dbSource]);

  useEffect(() => {
    if (!newOpen) return;
    const cod = String(
      articulo?.CODART || articulo?.REFERENCIA || pedidoRow?.CodArt || "",
    ).trim();
    if (!cod) {
      setArticuloMaestro(null);
      return;
    }
    let cancelled = false;
    searchA3Articulos(cod, dbSource)
      .then((data) => {
        if (cancelled) return;
        const ar = Array.isArray(data?.rows) ? data.rows : [];
        const hit =
          ar.find((x) => normStr(x.CODART ?? x.codart) === normStr(cod)) ??
          ar[0] ??
          null;
        setArticuloMaestro(hit || articulo || null);
      })
      .catch(() => {
        if (!cancelled) setArticuloMaestro(articulo || null);
      });
    return () => {
      cancelled = true;
    };
  }, [newOpen, articulo, pedidoRow?.CodArt, dbSource]);

  useEffect(() => {
    if (!newOpen || !articuloMaestro) return;
    const ah = normStr(
      articuloMaestro.CODFAMTALLAH ?? articuloMaestro.codfamtallah,
    );
    const av = normStr(
      articuloMaestro.CODFAMTALLAV ?? articuloMaestro.codfamtallav,
    );
    if (ah && av && ah !== av) {
      setMatrixFamiliaH(
        String(
          articuloMaestro.CODFAMTALLAH ?? articuloMaestro.codfamtallah ?? "",
        ).trim(),
      );
      setMatrixFamiliaV(
        String(
          articuloMaestro.CODFAMTALLAV ?? articuloMaestro.codfamtallav ?? "",
        ).trim(),
      );
    }
  }, [newOpen, articuloMaestro]);

  const artForPack = articuloMaestro || articulo;
  const artFamilyH = normStr(
    artForPack?.CODFAMTALLAH ?? artForPack?.codfamtallah ?? "",
  );
  const artFamilyV = normStr(
    artForPack?.CODFAMTALLAV ?? artForPack?.codfamtallav ?? "",
  );
  const lockFamiliesToArticulo = Boolean(artFamilyH && artFamilyV);
  const resolvedFamilyH = artFamilyH || normStr(matrixFamiliaH);
  const resolvedFamilyV = artFamilyV || normStr(matrixFamiliaV);

  const familiesColumnOptions = useMemo(() => {
    if (lockFamiliesToArticulo) {
      return familiasTallaCols.filter(
        (f) => normStr(f.CODFAMTALLA) === resolvedFamilyH,
      );
    }
    return sortFamiliesByKind(
      familiasTallaCols.filter(
        (f) =>
          !normStr(matrixFamiliaV) ||
          normStr(f.CODFAMTALLA) !== normStr(matrixFamiliaV),
      ),
      "talla",
    );
  }, [
    familiasTallaCols,
    lockFamiliesToArticulo,
    resolvedFamilyH,
    matrixFamiliaV,
  ]);

  const familiesRowOptions = useMemo(() => {
    if (lockFamiliesToArticulo) {
      return familiasTallaRows.filter(
        (f) => normStr(f.CODFAMTALLA) === resolvedFamilyV,
      );
    }
    return sortFamiliesByKind(
      familiasTallaRows.filter(
        (f) =>
          !normStr(matrixFamiliaH) ||
          normStr(f.CODFAMTALLA) !== normStr(matrixFamiliaH),
      ),
      "color",
    );
  }, [
    familiasTallaRows,
    lockFamiliesToArticulo,
    resolvedFamilyV,
    matrixFamiliaH,
  ]);

  const totalMatriz = useMemo(
    () =>
      lineasTallas.reduce((acc, row) => acc + matrizToNumber(row.unidades), 0),
    [lineasTallas],
  );

  const unitsCabeceraEstimate = useMemo(
    () => matrizToNumber(presupuesto?.Units),
    [presupuesto?.Units],
  );
  const mismatchMatrizVsEstimate =
    lineasTallas.length > 0 &&
    unitsCabeceraEstimate > 0 &&
    Math.abs(totalMatriz - unitsCabeceraEstimate) > 0.0001;

  const qtyForCell = (rowTalla, colTalla) => {
    const p = packLinepediFromGridPure(
      artForPack,
      matrixFamiliaH,
      matrixFamiliaV,
      colTalla,
      rowTalla,
    );
    if (!p) return 0;
    const r = lineasTallas.find((l) => lineasPackMatch(l, p));
    return r ? matrizToNumber(r.unidades) : 0;
  };

  const hasMatrixCell = (rowTalla, colTalla) => {
    const p = packLinepediFromGridPure(
      artForPack,
      matrixFamiliaH,
      matrixFamiliaV,
      colTalla,
      rowTalla,
    );
    if (!p) return false;
    return lineasTallas.some((l) => lineasPackMatch(l, p));
  };

  const updateMatrixCell = (rowTalla, colTalla, rawValue) => {
    const p = packLinepediFromGridPure(
      artForPack,
      matrixFamiliaH,
      matrixFamiliaV,
      colTalla,
      rowTalla,
    );
    if (!p) return;
    const unidades = matrizToNumber(rawValue);
    if (unidades < 0) return;
    setLineasTallas((prev) => {
      const idx = prev.findIndex((r) => lineasPackMatch(r, p));
      const nextRow = {
        codFamTallaH: p.codFamTallaH,
        codFamTallaV: p.codFamTallaV,
        codTallaH: normStr(p.codTallaH),
        codTallaV: normStr(p.codTallaV),
        unidades,
      };
      if (idx < 0) return [...prev, nextRow];
      const next = [...prev];
      next[idx] = nextRow;
      return next;
    });
  };

  const colTotals = useMemo(() => {
    if (!normStr(matrixFamiliaH) || !normStr(matrixFamiliaV))
      return tallasH.map(() => 0);
    return tallasH.map((h) =>
      tallasV.reduce((sum, v) => {
        const p = packLinepediFromGridPure(
          artForPack,
          matrixFamiliaH,
          matrixFamiliaV,
          h,
          v,
        );
        const r = p ? lineasTallas.find((l) => lineasPackMatch(l, p)) : null;
        return sum + (r ? matrizToNumber(r.unidades) : 0);
      }, 0),
    );
  }, [
    artForPack,
    tallasH,
    tallasV,
    lineasTallas,
    matrixFamiliaH,
    matrixFamiliaV,
  ]);

  /** Carga tallas/colores/unidades del pedido A3. `silentIfNoPedidoData`: no alerta si falta CodArt/IdPed (p. ej. apertura automática). */
  const loadMatrizFromPedidoA3 = useCallback(
    async (opts = {}) => {
      const silentIfNoPedidoData = Boolean(opts.silentIfNoPedidoData);
      const codart = String(pedidoRow?.CodArt || "").trim();
      const idPedc = pedidoRow?.IdPedc;
      const idPedv = pedidoRow?.IdPedv;
      if (!codart || (!idPedc && !idPedv)) {
        if (!silentIfNoPedidoData) {
          setError("No hay datos suficientes para copiar la matriz desde A3.");
        }
        return;
      }
      setLoadingMatriz(true);
      setError("");
      try {
        const data = await getA3PedidoLineasMatriz({
          idPedc,
          idPedv,
          codart,
          dbSource,
        });
        const rows = Array.isArray(data?.rows) ? data.rows : [];
        if (!rows.length) {
          setError("A3 no devolvió líneas de matriz para este pedido.");
          return;
        }
        const ar = await searchA3Articulos(codart, dbSource).catch(() => ({
          rows: [],
        }));
        const maestroHit =
          (Array.isArray(ar?.rows) ? ar.rows : []).find(
            (x) => normStr(x.CODART ?? x.codart) === normStr(codart),
          ) || null;
        if (maestroHit) setArticuloMaestro(maestroHit);
        setLineasTallas(
          rows.map((x) => ({
            codFamTallaH: String(x.codFamTallaH || "").trim(),
            codTallaH: String(x.codTallaH || "").trim(),
            codFamTallaV: String(x.codFamTallaV || "").trim(),
            codTallaV: String(x.codTallaV || "").trim(),
            unidades: matrizToNumber(x.unidades),
          })),
        );
        const ah = normStr(
          maestroHit?.CODFAMTALLAH ?? maestroHit?.codfamtallah,
        );
        const av = normStr(
          maestroHit?.CODFAMTALLAV ?? maestroHit?.codfamtallav,
        );
        if (ah && av && ah !== av) {
          setMatrixFamiliaH(
            String(
              maestroHit.CODFAMTALLAH ?? maestroHit.codfamtallah ?? "",
            ).trim(),
          );
          setMatrixFamiliaV(
            String(
              maestroHit.CODFAMTALLAV ?? maestroHit.codfamtallav ?? "",
            ).trim(),
          );
        } else {
          setMatrixFamiliaH(String(rows[0].codFamTallaH || "").trim());
          setMatrixFamiliaV(String(rows[0].codFamTallaV || "").trim());
        }
      } catch (e) {
        setError(e?.response?.data?.error || e.message);
      } finally {
        setLoadingMatriz(false);
      }
    },
    [pedidoRow, dbSource],
  );

  const openNuevoEnvio = () => {
    setError("");
    setLineasTallas([]);
    setMatrixFamiliaH("");
    setMatrixFamiliaV("");
    setArticuloMaestro(null);
    setHeader({
      ...emptyEnvioHeader(),
      POReference: poReferenceFromPedidoRow(pedidoRow),
    });
    setNewOpen(true);
    void prefillHeaderFromReferences();
    void loadMatrizFromPedidoA3({ silentIfNoPedidoData: true });
  };

  const autoOpenedForRef = useRef(null);
  useEffect(() => {
    if (!open) {
      autoOpenedForRef.current = null;
      return;
    }
    if (!autoOpenNuevo || !idPresupuestoA3Pedido) return;
    if (autoOpenedForRef.current === idPresupuestoA3Pedido) return;
    autoOpenedForRef.current = idPresupuestoA3Pedido;
    openNuevoEnvio();
  }, [open, autoOpenNuevo, idPresupuestoA3Pedido]);

  const handleCopiarMatrizPedido = () => {
    void loadMatrizFromPedidoA3();
  };

  const prefillHeaderFromReferences = useCallback(async () => {
    const pid = Number(productIdDisplay);
    if (!Number.isFinite(pid) || pid <= 0) return;
    setLoadingPrefill(true);
    try {
      const [legacyRows, timelineRows] = await Promise.all([
        getLogisticsByProductId(pid, {
          idPresupuesto: presupuesto?.IdPresupuesto,
        }).catch(() => []),
        getTimelineByProductId(pid, {
          idPresupuesto: presupuesto?.IdPresupuesto,
        }).catch(() => []),
      ]);
      const legacy =
        Array.isArray(legacyRows) && legacyRows.length ? legacyRows[0] : null;
      const timelineMap = new Map(
        (Array.isArray(timelineRows) ? timelineRows : []).map((row) => [
          String(row?.TaskName || "").trim(),
          row,
        ]),
      );
      setHeader((prev) => {
        const next = { ...prev };
        if (legacy) {
          next.ETA = inputDate(legacy.ETA) || next.ETA;
          next.BookingDone = inputDate(legacy.BookingDone) || next.BookingDone;
          next.DocumentsApproved =
            inputDate(legacy.DocumentsApproved) || next.DocumentsApproved;
          next.OriginalTelexDocsSemt =
            inputDate(legacy.OriginalTelexDocsSemt) ||
            next.OriginalTelexDocsSemt;
          next.ShipmentMethod = legacy.ShipmentMethod || next.ShipmentMethod;
          next.Forwarder = legacy.Forwarder || next.Forwarder;
        }
        if (!legacy) {
          for (const [taskName, field] of Object.entries(
            TIMELINE_DATE_TASK_TO_FIELD,
          )) {
            const picked = pickTimelineDate(timelineMap.get(taskName));
            if (picked && !next[field]) next[field] = picked;
          }
        }
        return next;
      });
    } finally {
      setLoadingPrefill(false);
    }
  }, [productIdDisplay, presupuesto?.IdPresupuesto]);

  const handleCreateEnvio = useCallback(async () => {
    if (!idPresupuestoA3Pedido) {
      setError("Falta pedido CRM (IdPresupuestoA3Pedido).");
      return;
    }
    const lineasPayload = buildEnvioLineasSavePayload(lineasTallas);

    setCreating(true);
    setError("");
    try {
      await createLogisticaEnvio({
        idPresupuestoA3Pedido,
        // No enviar productId: el backend lo resuelve desde el pedido CRM (evita 400 si el cliente mandaba IdPresupuesto por error).
        idPresupuesto: presupuesto?.IdPresupuesto || null,
        ETA: apiDateFromInput(header.ETA),
        Forwarder: normStr(header.Forwarder) || null,
        POReference: normStr(header.POReference) || null,
        Remarks: normStr(header.Remarks) || null,
        ShipmentMethod: normStr(header.ShipmentMethod) || null,
        NCartons: parseOptionalInt(header.NCartons),
        GrossWeightKgs: parseOptionalDecimal(header.GrossWeightKgs),
        TotalVolumeCbm: parseOptionalDecimal(header.TotalVolumeCbm),
        BookingDone: apiDateFromInput(header.BookingDone),
        InspectionDate: apiDateFromInput(header.InspectionDate),
        ExFD: apiDateFromInput(header.ExFD),
        ETDF: apiDateFromInput(header.ETDF),
        CutOff: apiDateFromInput(header.CutOff),
        ETD: apiDateFromInput(header.ETD),
        DD: apiDateFromInput(header.DD),
        ShipmentCodeCustomer: normStr(header.ShipmentCodeCustomer) || null,
        BLAWBNr: normStr(header.BLAWBNr) || null,
        DocumentsApproved: apiDateFromInput(header.DocumentsApproved),
        OriginalTelexDocsSemt: apiDateFromInput(header.OriginalTelexDocsSemt),
        lineas: lineasPayload,
      });
      setNewOpen(false);
      setHeader(emptyEnvioHeader());
      setLineasTallas([]);
      setMatrixFamiliaH("");
      setMatrixFamiliaV("");
      await load();
      await notifyChanged();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setCreating(false);
    }
  }, [
    idPresupuestoA3Pedido,
    presupuesto?.IdPresupuesto,
    header,
    lineasTallas,
    load,
    notifyChanged,
  ]);

  const forwarderSelectOptions = useMemo(() => {
    const current = String(header.Forwarder || "").trim();
    if (current && !forwarderOptions.includes(current)) {
      return [current, ...forwarderOptions];
    }
    return forwarderOptions;
  }, [forwarderOptions, header.Forwarder]);

  const handleDelete = useCallback(async () => {
    const id = Number(deleteEnvio?.IdLogisticaEnvio);
    if (!Number.isFinite(id) || id <= 0) return;
    setDeletingEnvio(true);
    setError("");
    try {
      await deleteLogisticaEnvio(id);
      if (Number(editEnvio?.IdLogisticaEnvio) === id) setEditEnvio(null);
      setDeleteEnvio(null);
      await load();
      await notifyChanged();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setDeletingEnvio(false);
    }
  }, [deleteEnvio, editEnvio?.IdLogisticaEnvio, load, notifyChanged]);

  if (!open) return null;
  const createShipmentMethodMeta = getShipmentMethodMeta(
    header.ShipmentMethod,
    theme,
  );
  const createSummaryCards = [
    {
      label: "PO",
      value: header.POReference || poReferenceFromPedidoRow(pedidoRow) || "—",
      color: theme.palette.primary.main,
      icon: <ReceiptLongOutlinedIcon sx={{ fontSize: 16 }} />,
    },
    {
      label: "Metodo",
      value: createShipmentMethodMeta.label,
      color: createShipmentMethodMeta.color,
      icon: createShipmentMethodMeta.icon,
    },
    {
      label: "ETD",
      value: inputDate(header.ETD) || "—",
      color: theme.palette.primary.dark,
      icon: <EventOutlinedIcon sx={{ fontSize: 16 }} />,
    },
    {
      label: "Booking",
      value: inputDate(header.BookingDone) || "—",
      color: theme.palette.warning.main,
      icon: <EventOutlinedIcon sx={{ fontSize: 16 }} />,
    },
    {
      label: "ETA",
      value: inputDate(header.ETA) || "—",
      color: theme.palette.info.main,
      icon: <EventOutlinedIcon sx={{ fontSize: 16 }} />,
    },
    {
      label: "DD",
      value: inputDate(header.DD) || "—",
      color: theme.palette.success.dark,
      icon: <EventOutlinedIcon sx={{ fontSize: 16 }} />,
    },
    {
      label: "Cartons",
      value: header.NCartons || "—",
      color: theme.palette.info.main,
      icon: <Inventory2OutlinedIcon sx={{ fontSize: 16 }} />,
    },
    {
      label: "Kg",
      value: header.GrossWeightKgs || "—",
      color: theme.palette.error.main,
      icon: <ReceiptLongOutlinedIcon sx={{ fontSize: 16 }} />,
    },
    {
      label: "CBM",
      value: header.TotalVolumeCbm || "—",
      color: theme.palette.success.main,
      icon: <Inventory2OutlinedIcon sx={{ fontSize: 16 }} />,
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 12px 36px rgba(0,0,0,0.5)"
              : "0 12px 36px rgba(0,0,0,0.18)",
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1.2,
          borderBottom: "1px solid",
          borderColor: "divider",
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.info.main, 0.08)} 100%)`,
        }}
      >
        Envíos / logística — pedido{" "}
        {pedidoRow?.Referencia || idPresupuestoA3Pedido || ""}
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("logisticaPedidos.pedidoEnviosIntro")}
        </Alert>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Typography variant="body2" color="text.secondary">
            ProductId: {productIdDisplay || "—"}
          </Typography>
          <Button variant="contained" size="small" onClick={openNuevoEnvio}>
            Nuevo envío
          </Button>
        </Stack>
        {loading ? (
          <Typography variant="body2">Cargando…</Typography>
        ) : envios.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No hay envíos. Cree uno con la matriz talla/color o copie desde A3.
          </Typography>
        ) : (
          <Stack spacing={2}>
            {envios.map((e) => {
              const linksCount = Array.isArray(e?.pedidoLinks)
                ? e.pedidoLinks.length
                : 0;
              const cabeceraUI = cabeceraFromEnvioYConsolidacion(
                e,
                consolidacionByEnvio[e.IdLogisticaEnvio],
              );
              return (
                <Box
                  key={e.IdLogisticaEnvio}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: alpha(
                      theme.palette.background.paper,
                      0.92,
                    ),
                    boxShadow: `0 6px 20px ${alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.35 : 0.08)}`,
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Stack spacing={0.45}>
                      <Typography
                        variant="subtitle1"
                        fontWeight={800}
                        sx={{ lineHeight: 1.15 }}
                      >
                        Envío #{e.IdLogisticaEnvio}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Resumen de carga y documentación
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        size="small"
                        color="primary"
                        aria-label="Editar envío"
                        onClick={() => setEditEnvio(e)}
                        sx={{
                          border: "1px solid",
                          borderColor: "primary.main",
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.08,
                          ),
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        aria-label="Eliminar envío"
                        onClick={() => setDeleteEnvio(e)}
                        sx={{
                          border: "1px solid",
                          borderColor: "error.main",
                          backgroundColor: alpha(
                            theme.palette.error.main,
                            0.06,
                          ),
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                  {Array.isArray(e.pedidoLinks) && e.pedidoLinks.length > 0 ? (
                    <Stack
                      direction="row"
                      gap={0.75}
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ mb: 1 }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ width: "100%", fontWeight: 700 }}
                      >
                        {t("logisticaPedidos.envioPedidosEnCarga")}:
                      </Typography>
                      {e.pedidoLinks.map((lnk) => {
                        const p = lnk?.pedidoA3;
                        const idp = Number(
                          p?.IdPresupuestoA3Pedido ??
                            lnk?.IdPresupuestoA3Pedido,
                        );
                        const isCtx =
                          Number(idPresupuestoA3Pedido) > 0 &&
                          idp === Number(idPresupuestoA3Pedido);
                        const label = p?.Referencia
                          ? `#${idp} · ${p.Referencia}`
                          : `#${idp}`;
                        const pidProd = p?.presupuesto?.IdProducto;
                        return (
                          <Chip
                            key={lnk.Id ?? `${e.IdLogisticaEnvio}-${idp}`}
                            size="small"
                            variant={isCtx ? "filled" : "outlined"}
                            color={isCtx ? "primary" : "default"}
                            label={
                              pidProd != null && String(pidProd).trim() !== ""
                                ? `${label} · IdProducto ${pidProd}`
                                : label
                            }
                          />
                        );
                      })}
                    </Stack>
                  ) : null}
                  <Box
                    sx={{
                      mt: 1.1,
                      p: 1.2,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1.5,
                      backgroundColor: alpha(
                        theme.palette.background.default,
                        0.55,
                      ),
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 800, display: "block", mb: 0.8 }}
                    >
                      Resumen del pedido
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(2, minmax(0, 1fr))",
                          md: "repeat(4, minmax(0, 1fr))",
                        },
                        gap: 0.9,
                      }}
                    >
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`PO: ${cabeceraUI.POReference || "—"}`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Método: ${cabeceraUI.ShipmentMethod || "—"}`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`ETA: ${fmtEnvioDate(cabeceraUI.ETA)}`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Inspection: ${fmtEnvioDate(cabeceraUI.InspectionDate)}`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`ExFD: ${fmtEnvioDate(cabeceraUI.ExFD)}`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`ETDF: ${fmtEnvioDate(cabeceraUI.ETDF)}`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`CUT-OFF: ${fmtEnvioDate(cabeceraUI.CutOff)}`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`ETD: ${fmtEnvioDate(cabeceraUI.ETD)}`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`DD: ${fmtEnvioDate(cabeceraUI.DD)}`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Forwarder: ${cabeceraUI.Forwarder || "—"}`}
                      />
                      <Chip
                        size="small"
                        color="info"
                        variant="outlined"
                        label={`Cartons: ${cabeceraUI.NCartons ?? "—"}`}
                      />
                      <Chip
                        size="small"
                        color="info"
                        variant="outlined"
                        label={`Kg: ${cabeceraUI.GrossWeightKgs ?? "—"}`}
                      />
                      <Chip
                        size="small"
                        color="info"
                        variant="outlined"
                        label={`CBM: ${cabeceraUI.TotalVolumeCbm ?? "—"}`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Booking: ${fmtEnvioDate(cabeceraUI.BookingDone)}`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Cód. cliente: ${cabeceraUI.ShipmentCodeCustomer || "—"}`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`BL/AWB: ${cabeceraUI.BLAWBNr || "—"}`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Docs aprobadas: ${fmtEnvioDate(cabeceraUI.DocumentsApproved)}`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Telex/orig.: ${fmtEnvioDate(cabeceraUI.OriginalTelexDocsSemt)}`}
                      />
                    </Box>
                    {cabeceraUI.Remarks ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 1 }}
                      >
                        <strong>Remarks:</strong> {String(cabeceraUI.Remarks)}
                      </Typography>
                    ) : null}
                  </Box>
                  {linksCount < 2 ? (
                    <>
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{ display: "block", mt: 1.5 }}
                      >
                        Matriz tallas / colores
                      </Typography>
                      <EnvioMatrizLectura lineas={e.lineas} />
                    </>
                  ) : (
                    <Box sx={{ mt: 1.25 }}>
                      <LogisticaPedidosMatricesReferencia
                        pedidoLinks={e.pedidoLinks}
                        fallbackDb={dbSource}
                        envioLineas={Array.isArray(e?.lineas) ? e.lineas : []}
                        showEnvioConsolidada={false}
                      />
                    </Box>
                  )}
                  <Box sx={{ mt: 2, width: "100%" }}>
                    <DocumentosLogistica
                      logisticaEnvioId={e.IdLogisticaEnvio}
                    />
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions
        sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "divider" }}
      >
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>

      <LogisticaEnvioEditDialog
        open={Boolean(editEnvio)}
        envio={editEnvio}
        onClose={() => setEditEnvio(null)}
        onSaved={async () => {
          await load();
          await notifyChanged();
        }}
        dbSource={dbSource}
        pedidoRow={pedidoRow}
        articulo={articulo}
        presupuesto={presupuesto}
        currentPedidoId={null}
      />
      <Dialog
        open={Boolean(deleteEnvio)}
        onClose={() => {
          if (!deletingEnvio) setDeleteEnvio(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Eliminar envío</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Se eliminará el envío #{deleteEnvio?.IdLogisticaEnvio ?? ""} con sus
            líneas y vínculos. Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "divider" }}
        >
          <Button onClick={() => setDeleteEnvio(null)} disabled={deletingEnvio}>
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deletingEnvio}
          >
            {deletingEnvio ? "Eliminando..." : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        maxWidth="lg"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 3,
            py: 2.1,
            borderBottom: "1px solid",
            borderColor: alpha(theme.palette.primary.main, 0.12),
            background: `linear-gradient(135deg, ${alpha(createShipmentMethodMeta.color, 0.16)} 0%, ${alpha(theme.palette.success.main, 0.08)} 52%, ${alpha(theme.palette.background.paper, 0.94)} 100%)`,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={{ xs: 1.25, md: 2 }}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1.4} alignItems="flex-start">
              <Box
                sx={{
                  width: 46,
                  height: 46,
                  borderRadius: 2.4,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: createShipmentMethodMeta.color,
                  backgroundColor: alpha(createShipmentMethodMeta.color, 0.14),
                  border: "1px solid",
                  borderColor: alpha(createShipmentMethodMeta.color, 0.2),
                  boxShadow: `0 8px 20px ${alpha(createShipmentMethodMeta.color, 0.16)}`,
                  flexShrink: 0,
                }}
              >
                {createShipmentMethodMeta.icon}
              </Box>
              <Box>
                <Stack
                  direction="row"
                  gap={0.75}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mb: 0.55 }}
                >
                  <Chip size="small" label="Nuevo envio" color="primary" />
                  {pedidoRow?.Referencia ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={pedidoRow.Referencia}
                    />
                  ) : null}
                  {productIdDisplay ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      color="secondary"
                      label={`IdProducto ${productIdDisplay}`}
                    />
                  ) : null}
                </Stack>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 800, lineHeight: 1.1 }}
                >
                  Nuevo envío
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.45 }}
                >
                  Prepara la cabecera logistica, el transporte y la matriz
                  inicial del envio.
                </Typography>
              </Box>
            </Stack>
            <Box
              sx={{
                minWidth: { xs: "100%", md: 180 },
                px: 1.3,
                py: 1,
                borderRadius: 2,
                border: "1px solid",
                borderColor: alpha(createShipmentMethodMeta.color, 0.16),
                backgroundColor: alpha(theme.palette.background.paper, 0.76),
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  color: "text.secondary",
                }}
              >
                Transporte
              </Typography>
              <Stack
                direction="row"
                spacing={0.8}
                alignItems="center"
                sx={{ mt: 0.45 }}
              >
                <Box
                  sx={{
                    color: createShipmentMethodMeta.color,
                    display: "flex",
                  }}
                >
                  {createShipmentMethodMeta.icon}
                </Box>
                <Typography
                  sx={{
                    fontWeight: 800,
                    color: createShipmentMethodMeta.color,
                  }}
                >
                  {createShipmentMethodMeta.label}
                </Typography>
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.4 }}
              >
                Total matriz actual: {fmtQtyMatriz(totalMatriz)} uds
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ backgroundColor: alpha(theme.palette.background.default, 0.5) }}
        >
          <Stack spacing={2.5} sx={{ mt: 0.5 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleCopiarMatrizPedido}
              disabled={loadingMatriz}
              startIcon={<Inventory2OutlinedIcon fontSize="small" />}
              sx={{
                alignSelf: "flex-start",
                borderRadius: 999,
                px: 1.5,
                backgroundColor: alpha(theme.palette.background.paper, 0.82),
              }}
            >
              {loadingMatriz ? "Copiando…" : "Copiar matriz del pedido (A3)"}
            </Button>
            <Box
              sx={{
                p: 1.45,
                border: "1px solid",
                borderColor: alpha(theme.palette.primary.main, 0.12),
                borderRadius: 2.4,
                background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.035)} 0%, ${alpha(theme.palette.background.paper, 0.94)} 100%)`,
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "0 10px 28px rgba(0,0,0,0.18)"
                    : "0 10px 28px rgba(15,23,42,0.04)",
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.25}
                justifyContent="space-between"
                sx={{ mb: 1.15 }}
              >
                <Box>
                  <Stack
                    direction="row"
                    spacing={0.8}
                    alignItems="center"
                    sx={{ mb: 0.35 }}
                  >
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: 1.8,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: theme.palette.primary.main,
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.12,
                        ),
                      }}
                    >
                      <AssignmentOutlinedIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      Resumen del envio
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Vista rapida de la cabecera antes de guardar el envio.
                  </Typography>
                </Box>
                {loadingPrefill ? (
                  <Alert severity="info" sx={{ py: 0, borderRadius: 2 }}>
                    Cargando fechas de referencia desde Timeline / Legacy...
                  </Alert>
                ) : null}
              </Stack>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    md: "repeat(4, minmax(0, 1fr))",
                  },
                  gap: 0.9,
                }}
              >
                {createSummaryCards.map((item) => (
                  <Box
                    key={item.label}
                    sx={{
                      borderRadius: 1.8,
                      border: "1px solid",
                      borderColor: alpha(item.color, 0.14),
                      backgroundColor: alpha(
                        item.color,
                        theme.palette.mode === "dark" ? 0.14 : 0.05,
                      ),
                      px: 1.05,
                      py: 0.95,
                      minHeight: 68,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                  >
                    <Stack direction="row" spacing={0.7} alignItems="center">
                      <Box
                        sx={{
                          color: item.color,
                          display: "flex",
                          flexShrink: 0,
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "0.68rem",
                          textTransform: "uppercase",
                          letterSpacing: 0.35,
                          fontWeight: 700,
                          color: "text.secondary",
                        }}
                      >
                        {item.label}
                      </Typography>
                    </Stack>
                    <Typography
                      sx={{
                        mt: 0.7,
                        fontSize: "0.9rem",
                        fontWeight: 800,
                        color: item.color,
                        lineHeight: 1.2,
                        wordBreak: "break-word",
                      }}
                    >
                      {item.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
            <Stack direction="row" spacing={0.9} alignItems="center">
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: createShipmentMethodMeta.color,
                  backgroundColor: alpha(createShipmentMethodMeta.color, 0.12),
                }}
              >
                {createShipmentMethodMeta.icon}
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Cabecera logistica
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Campos principales del transporte y documentacion del envio.
                </Typography>
              </Box>
            </Stack>
            <Box
              sx={{
                p: 1.7,
                border: "1px solid",
                borderColor: alpha(createShipmentMethodMeta.color, 0.14),
                borderRadius: 2.4,
                background: `linear-gradient(180deg, ${alpha(createShipmentMethodMeta.color, 0.035)} 0%, ${alpha(theme.palette.background.paper, 0.96)} 100%)`,
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                },
                gap: 1.5,
              }}
            >
              <TextField
                sx={softFieldSx}
                label="Shipment method"
                value={header.ShipmentMethod}
                select
                SelectProps={{ native: true }}
                size="small"
                onChange={(ev) =>
                  setHeader((h) => ({ ...h, ShipmentMethod: ev.target.value }))
                }
              >
                <option value="">—</option>
                {SHIPMENT_METHOD_OPTIONS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </TextField>
              <TextField
                sx={softFieldSx}
                label="Forwarder"
                select
                SelectProps={{ native: true }}
                size="small"
                value={header.Forwarder}
                onChange={(ev) =>
                  setHeader((h) => ({ ...h, Forwarder: ev.target.value }))
                }
              >
                <option value="">—</option>
                {forwarderSelectOptions.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </TextField>
              <TextField
                sx={softFieldSx}
                label="PO Reference"
                size="small"
                value={header.POReference}
                onChange={(ev) =>
                  setHeader((h) => ({ ...h, POReference: ev.target.value }))
                }
                helperText="Por defecto: referencia del pedido"
              />
              <TextField
                sx={softFieldSx}
                label="Shipment code (cliente)"
                size="small"
                value={header.ShipmentCodeCustomer}
                onChange={(ev) =>
                  setHeader((h) => ({
                    ...h,
                    ShipmentCodeCustomer: ev.target.value,
                  }))
                }
              />
              <TextField
                sx={softFieldSx}
                label="BL / AWB Nr"
                size="small"
                value={header.BLAWBNr}
                onChange={(ev) =>
                  setHeader((h) => ({ ...h, BLAWBNr: ev.target.value }))
                }
              />
              <TextField
                sx={softFieldSx}
                label="Nº cartons"
                size="small"
                type="number"
                inputProps={{ min: 0, step: 1 }}
                value={header.NCartons}
                onChange={(ev) =>
                  setHeader((h) => ({ ...h, NCartons: ev.target.value }))
                }
              />
              <TextField
                sx={softFieldSx}
                label="Gross weight (kg)"
                size="small"
                value={header.GrossWeightKgs}
                onChange={(ev) =>
                  setHeader((h) => ({ ...h, GrossWeightKgs: ev.target.value }))
                }
                inputProps={{ inputMode: "decimal" }}
              />
              <TextField
                sx={softFieldSx}
                label="Total volume (CBM)"
                size="small"
                value={header.TotalVolumeCbm}
                onChange={(ev) =>
                  setHeader((h) => ({ ...h, TotalVolumeCbm: ev.target.value }))
                }
                inputProps={{ inputMode: "decimal" }}
              />
              <TextField
                sx={softFieldSx}
                label="ETD"
                type="date"
                size="small"
                value={header.ETD}
                onChange={(ev) =>
                  setHeader((h) => ({ ...h, ETD: ev.target.value }))
                }
                InputLabelProps={{ shrink: true }}
              />
              {CRITICAL_DATE_INPUTS.map((item) => (
                <TextField
                  key={item.field}
                  sx={softFieldSx}
                  label={item.label}
                  type="date"
                  size="small"
                  value={header[item.field]}
                  onChange={(ev) =>
                    setHeader((h) => ({ ...h, [item.field]: ev.target.value }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
              ))}
              <TextField
                sx={softFieldSx}
                label="Booking done"
                type="date"
                size="small"
                value={header.BookingDone}
                onChange={(ev) =>
                  setHeader((h) => ({ ...h, BookingDone: ev.target.value }))
                }
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                sx={softFieldSx}
                label="ETA"
                type="date"
                size="small"
                value={header.ETA}
                onChange={(ev) =>
                  setHeader((h) => ({ ...h, ETA: ev.target.value }))
                }
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                sx={softFieldSx}
                label="DD"
                title="Delivery Date"
                type="date"
                size="small"
                value={header.DD}
                onChange={(ev) =>
                  setHeader((h) => ({ ...h, DD: ev.target.value }))
                }
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                sx={softFieldSx}
                label="Documents approved"
                type="date"
                size="small"
                value={header.DocumentsApproved}
                onChange={(ev) =>
                  setHeader((h) => ({
                    ...h,
                    DocumentsApproved: ev.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                sx={softFieldSx}
                label="Original telex / docs sent"
                type="date"
                size="small"
                value={header.OriginalTelexDocsSemt}
                onChange={(ev) =>
                  setHeader((h) => ({
                    ...h,
                    OriginalTelexDocsSemt: ev.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <TextField
              sx={{
                ...softFieldSx,
                "& .MuiInputBase-root": {
                  ...softFieldSx["& .MuiInputBase-root"],
                  borderRadius: 2.2,
                },
              }}
              label="Remarks"
              size="small"
              multiline
              minRows={2}
              fullWidth
              value={header.Remarks}
              onChange={(ev) =>
                setHeader((h) => ({ ...h, Remarks: ev.target.value }))
              }
            />

            <Box
              sx={{
                p: 1.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.background.paper, 0.86),
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  display: "block",
                  fontWeight: 700,
                  mb: 0.5,
                }}
              >
                Matriz tallas / colores
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 1 }}
              >
                Arriba = tallas (familia columnas), izquierda = colores (familia
                filas). Misma lógica que en pedidos A3.
              </Typography>
              {loadingMatriz ? (
                <Alert severity="info" sx={{ mb: 1 }}>
                  Cargando desglose desde A3…
                </Alert>
              ) : null}
              {normStr(matrixFamiliaH) &&
              normStr(matrixFamiliaV) &&
              normStr(matrixFamiliaH) === normStr(matrixFamiliaV) ? (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  La misma familia en columnas y filas: elegí otra familia en
                  uno de los desplegables para distinguir tallas y colores.
                </Alert>
              ) : null}
              {mismatchMatrizVsEstimate ? (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  La suma de la matriz ({fmtQtyMatriz(totalMatriz)}) no coincide
                  con Units del estimate ({fmtQtyMatriz(unitsCabeceraEstimate)}
                  ).
                </Alert>
              ) : null}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
                  gap: 1.25,
                  mb: 1.25,
                }}
              >
                <TextField
                  label="Familia de tallas (columnas)"
                  value={matrixFamiliaH}
                  select
                  SelectProps={{ native: true }}
                  disabled={lockFamiliesToArticulo}
                  size="small"
                  onChange={(e) => {
                    setMatrixFamiliaH(e.target.value);
                    setLineasTallas([]);
                  }}
                >
                  <option value=""></option>
                  {familiesColumnOptions.map((f) => (
                    <option key={`fh-${f.CODFAMTALLA}`} value={f.CODFAMTALLA}>
                      {f.CODFAMTALLA} - {f.DESCFAMTALLA}
                    </option>
                  ))}
                </TextField>
                <TextField
                  label="Familia de colores (filas)"
                  value={matrixFamiliaV}
                  select
                  SelectProps={{ native: true }}
                  disabled={lockFamiliesToArticulo}
                  size="small"
                  onChange={(e) => {
                    setMatrixFamiliaV(e.target.value);
                    setLineasTallas([]);
                  }}
                >
                  <option value=""></option>
                  {familiesRowOptions.map((f) => (
                    <option key={`fv-${f.CODFAMTALLA}`} value={f.CODFAMTALLA}>
                      {f.CODFAMTALLA} - {f.DESCFAMTALLA}
                    </option>
                  ))}
                </TextField>
              </Box>
              {lockFamiliesToArticulo ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.75 }}
                >
                  Familias según artículo A3: columnas{" "}
                  <strong>{resolvedFamilyH}</strong>, filas{" "}
                  <strong>{resolvedFamilyV}</strong>.
                </Typography>
              ) : null}
              {!matrixFamiliaH || !matrixFamiliaV ? (
                <Typography variant="caption" color="text.secondary">
                  Elegí las dos familias o usá «Copiar matriz del pedido».
                </Typography>
              ) : tallasH.length === 0 || tallasV.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  No hay tallas en catálogo para esas familias (o aún se están
                  cargando).
                </Typography>
              ) : (
                <Box sx={{ overflowX: "auto", maxWidth: "100%" }}>
                  <Table size="small" sx={{ minWidth: 520 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            position: "sticky",
                            left: 0,
                            bgcolor: "background.paper",
                            zIndex: 1,
                            fontWeight: 700,
                          }}
                        >
                          Lateral / Superior
                        </TableCell>
                        {tallasH.map((h) => (
                          <TableCell
                            key={`ch-${String(h.CODTALLA)}`}
                            align="right"
                          >
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 600 }}
                            >
                              {tallaLabel(h)}
                            </Typography>
                          </TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          Σ fila (uds)
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tallasV.map((v) => {
                        const rowSum = tallasH.reduce(
                          (s, h) => s + qtyForCell(v, h),
                          0,
                        );
                        return (
                          <TableRow key={`vr-${String(v.CODTALLA)}`}>
                            <TableCell
                              sx={{
                                position: "sticky",
                                left: 0,
                                bgcolor: "background.paper",
                                zIndex: 1,
                                whiteSpace: "nowrap",
                                fontWeight: 600,
                              }}
                            >
                              {colorLabel(v)}
                            </TableCell>
                            {tallasH.map((h) => {
                              const q = qtyForCell(v, h);
                              return (
                                <TableCell
                                  key={`c-${String(v.CODTALLA)}-${String(h.CODTALLA)}`}
                                  align="right"
                                  sx={{ py: 0.5, minWidth: 72 }}
                                >
                                  <TextField
                                    size="small"
                                    value={hasMatrixCell(v, h) ? String(q) : ""}
                                    placeholder="0"
                                    inputProps={{
                                      inputMode: "decimal",
                                      style: {
                                        textAlign: "right",
                                        padding: "4px 6px",
                                      },
                                    }}
                                    onChange={(e) =>
                                      updateMatrixCell(v, h, e.target.value)
                                    }
                                  />
                                </TableCell>
                              );
                            })}
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              {fmtQtyMatriz(rowSum)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Σ columna (uds)
                        </TableCell>
                        {colTotals.map((ct, i) => (
                          <TableCell
                            key={`ft-${i}`}
                            align="right"
                            sx={{ fontWeight: 600 }}
                          >
                            {fmtQtyMatriz(ct)}
                          </TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {fmtQtyMatriz(totalMatriz)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
              )}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1 }}
              >
                Total matriz: <strong>{fmtQtyMatriz(totalMatriz)} uds</strong>
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid",
            borderColor: alpha(theme.palette.primary.main, 0.12),
            backgroundColor: alpha(theme.palette.background.paper, 0.92),
          }}
        >
          <Button
            onClick={() => setNewOpen(false)}
            sx={{ borderRadius: 999, px: 1.8 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={creating}
            onClick={handleCreateEnvio}
            sx={{
              borderRadius: 999,
              px: 2.1,
              boxShadow: `0 10px 24px ${alpha(theme.palette.primary.main, 0.24)}`,
            }}
          >
            Guardar envío
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default PedidoEnviosDialog;
